<template>
  <div
    ref="formItemRef"
    :class="formItemClasses"
    data-test-name="form-item"
    data-test-variant=""
    data-test-state=""
  >
    <label v-if="label || $slots.label" :class="formItemLabelClasses">
      <slot name="label" :label="label">
        {{ label }}
        <span v-if="optional">{{ t('b.formItem.optional') }}</span>
        <span v-if="required" style="color: var(--b-color-red-4)">*</span>
      </slot>
    </label>
    <slot>
      <p :class="ns.e('value')">{{ value }}</p>
    </slot>
    <p v-if="error" :class="ns.e('error')">
      <b-icon :size="14">
        <Error />
      </b-icon>
      <span>{{ error }}</span>
    </p>
    <p v-else-if="helpText" :class="ns.e('help-text')">
      <b-icon :size="14">
        <Information />
      </b-icon>
      <span>{{ helpText }}</span>
    </p>
  </div>
</template>

<script lang="ts" setup>
import { computed, provide, reactive, ref, toRefs } from 'vue'
import { formItemContextKey } from '@bigin/tokens'
import { useLocale, useNamespace, useSize } from '@bigin/hooks'
import { Error, Information } from '@bigin/icons-vue'
import BIcon from '@bigin/components/icon'
import { formItemProps } from './form-item'

import type { FormItemContext } from '@bigin/tokens'

defineOptions({
  name: 'BFormItem',
})
const props = defineProps(formItemProps)

const _size = useSize(undefined, { formItem: false })
const ns = useNamespace('form-item')
const focused = ref(false)

const formItemRef = ref<HTMLDivElement>()

const { t } = useLocale()

const formItemClasses = computed(() => [
  ns.b(),
  ns.m(_size.value),
  ns.is('error', !!props.error),
  ns.is('focused', focused.value),
  ns.is('topless', props.topless),
])

const formItemLabelClasses = computed(() => [
  ns.e('label'),
  ns.is('optional', props.optional),
  ns.is('required', props.required),
])

const setFocused = (value: boolean) => (focused.value = value)

const context: FormItemContext = reactive({
  ...toRefs(props),
  $el: formItemRef,
  size: _size,
  setFocused,
})

provide(formItemContextKey, context)

defineExpose({
  /** @description form item size */
  size: _size,
  focused,
})
</script>
